<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import VariableMappingModal from 'dashboard/components-next/whatsapp/VariableMappingModal.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const formDialogRef = ref(null);
const deleteDialogRef = ref(null);
const mappingModalRef = ref(null);
const selectedTemplate = ref(null);
const selectedMappingTemplate = ref(null);
const currentPage = ref(1);
const filterStatus = ref('');
const filterName = ref('');
const formError = ref('');
const form = ref({ name: '', category: '', language: '', components: '[]' });

const templates = computed(
  () => getters['whatsappTemplates/getTemplates'].value
);
const meta = computed(() => getters['whatsappTemplates/getMeta'].value);
const uiFlags = computed(() => getters['whatsappTemplates/getUIFlags'].value);
const isEditing = computed(() => !!selectedTemplate.value);

const fetchTemplates = () => {
  const params = { page: currentPage.value };
  if (filterStatus.value) params.status = filterStatus.value;
  if (filterName.value) params.name = filterName.value;
  store
    .dispatch('whatsappTemplates/fetch', {
      inboxId: props.inbox.id,
      params,
    })
    .catch(() => {});
};

const syncAndFetch = inboxId => {
  store.dispatch('whatsappTemplates/syncAndFetch', { inboxId }).catch(() => {});
};

watch(() => props.inbox.id, syncAndFetch, { immediate: true });

const openCreate = () => {
  selectedTemplate.value = null;
  form.value = { name: '', category: '', language: '', components: '[]' };
  formError.value = '';
  formDialogRef.value?.open();
};

const openEdit = template => {
  selectedTemplate.value = template;
  form.value = {
    name: template.name,
    category: template.category,
    language: template.language,
    components: JSON.stringify(template.components || []),
  };
  formError.value = '';
  formDialogRef.value?.open();
};

const confirmDelete = template => {
  selectedTemplate.value = template;
  deleteDialogRef.value?.open();
};

const openMapping = template => {
  selectedMappingTemplate.value = template;
  mappingModalRef.value?.open();
};

const handleFormConfirm = async () => {
  formError.value = '';
  let components;
  try {
    components = JSON.parse(form.value.components);
  } catch {
    formError.value = 'Invalid JSON in components field.';
    return;
  }
  const templateData = { ...form.value, components };
  try {
    if (isEditing.value) {
      await store.dispatch('whatsappTemplates/update', {
        inboxId: props.inbox.id,
        templateId: selectedTemplate.value.id,
        templateData,
      });
      useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('whatsappTemplates/create', {
        inboxId: props.inbox.id,
        templateData,
      });
      useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.CREATE_SUCCESS'));
    }
    formDialogRef.value?.close();
  } catch (e) {
    formError.value =
      e.response?.data?.error ||
      e.message ||
      t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.CREATE_ERROR');
  }
};

const handleDeleteConfirm = async () => {
  try {
    await store.dispatch('whatsappTemplates/destroy', {
      inboxId: props.inbox.id,
      templateId: selectedTemplate.value.id,
    });
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.DELETE_SUCCESS'));
    deleteDialogRef.value?.close();
  } catch (e) {
    useAlert(
      e.response?.data?.error ||
        e.message ||
        t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.DELETE_ERROR')
    );
  }
};

const onPageChange = page => {
  currentPage.value = page;
  fetchTemplates();
};

const applyFilters = () => {
  currentPage.value = 1;
  fetchTemplates();
};

const statusBadgeClass = status => {
  const map = {
    APPROVED: 'bg-green-100 text-green-700',
    PENDING: 'bg-yellow-100 text-yellow-700',
    REJECTED: 'bg-red-100 text-red-700',
    DELETED: 'bg-gray-100 text-gray-500',
  };
  return map[status] || 'bg-gray-100 text-gray-500';
};
</script>

<template>
  <div class="flex flex-col gap-4 p-8">
    <div class="flex items-center justify-between">
      <h2 class="text-lg font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.TITLE') }}
      </h2>
      <Button
        :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.CREATE')"
        @click="openCreate"
      />
    </div>

    <div class="flex gap-3">
      <select v-model="filterStatus" class="w-44" @change="applyFilters">
        <option value="">
          {{
            t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.FILTER.STATUS_PLACEHOLDER')
          }}
        </option>
        <option value="APPROVED">
          {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.STATUS.APPROVED') }}
        </option>
        <option value="PENDING">
          {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.STATUS.PENDING') }}
        </option>
        <option value="REJECTED">
          {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.STATUS.REJECTED') }}
        </option>
      </select>
      <input
        v-model="filterName"
        type="text"
        class="w-64 mb-0"
        :placeholder="
          t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.FILTER.SEARCH_PLACEHOLDER')
        "
        @keyup.enter="applyFilters"
      />
      <Button
        variant="faded"
        color="slate"
        :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.FILTER.APPLY')"
        @click="applyFilters"
      />
    </div>

    <div v-if="uiFlags.isFetching" class="py-10 text-center text-n-slate-11">
      {{ t('LOADING') }}
    </div>

    <div
      v-else-if="!templates.length"
      class="py-10 text-center text-n-slate-11"
    >
      {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.EMPTY_STATE') }}
    </div>

    <div v-else class="overflow-x-auto rounded-lg border border-n-weak">
      <table class="w-full text-sm">
        <thead class="bg-n-alpha-2 text-n-slate-11">
          <tr>
            <th class="px-4 py-3 text-left font-medium">
              {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.TABLE.NAME') }}
            </th>
            <th class="px-4 py-3 text-left font-medium">
              {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.TABLE.CATEGORY') }}
            </th>
            <th class="px-4 py-3 text-left font-medium">
              {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.TABLE.LANGUAGE') }}
            </th>
            <th class="px-4 py-3 text-left font-medium">
              {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.TABLE.STATUS') }}
            </th>
            <th class="px-4 py-3 text-left font-medium">
              {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.TABLE.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr
            v-for="template in templates"
            :key="template.id"
            class="hover:bg-n-alpha-1"
          >
            <td class="px-4 py-3 font-mono text-n-slate-12">
              {{ template.name }}
            </td>
            <td class="px-4 py-3 text-n-slate-11">{{ template.category }}</td>
            <td class="px-4 py-3 text-n-slate-11">{{ template.language }}</td>
            <td class="px-4 py-3">
              <span
                class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                :class="statusBadgeClass(template.status)"
              >
                {{
                  t(
                    `INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.STATUS.${template.status}`
                  )
                }}
              </span>
            </td>
            <td class="px-4 py-3">
              <div class="flex gap-2">
                <Button
                  variant="faded"
                  color="slate"
                  sm
                  :label="
                    t('WHATSAPP_TEMPLATES.VARIABLE_MAPPING.CONFIGURE_BUTTON')
                  "
                  @click="openMapping(template)"
                />
                <Button
                  v-if="template.status === 'REJECTED'"
                  variant="faded"
                  color="slate"
                  sm
                  :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.EDIT')"
                  @click="openEdit(template)"
                />
                <Button
                  variant="faded"
                  color="ruby"
                  sm
                  :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.DELETE')"
                  @click="confirmDelete(template)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <TableFooter
      v-if="meta.total_count > 0"
      :current-page="currentPage"
      :total-count="meta.total_count"
      :page-size="25"
      class="border-n-weak border-t !px-0 py-4"
      @page-change="onPageChange"
    />

    <Dialog
      ref="formDialogRef"
      :title="
        isEditing
          ? t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.EDIT')
          : t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.CREATE')
      "
      :confirm-button-label="
        isEditing
          ? t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.UPDATE_BUTTON')
          : t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.CREATE_BUTTON')
      "
      :is-loading="uiFlags.isCreating || uiFlags.isUpdating"
      width="xl"
      overflow-y-auto
      @confirm="handleFormConfirm"
    >
      <div class="flex flex-col gap-4">
        <div>
          <label class="mb-1 block text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.FORM.NAME.LABEL') }}
          </label>
          <input
            v-model="form.name"
            type="text"
            class="mb-0"
            :disabled="isEditing"
            :placeholder="
              t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.FORM.NAME.PLACEHOLDER')
            "
          />
          <p class="mt-1 text-xs text-n-slate-11">
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.FORM.NAME.HINT') }}
          </p>
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.FORM.CATEGORY.LABEL') }}
          </label>
          <select v-model="form.category" class="mb-0">
            <option value="">
              {{
                t(
                  'INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.FORM.CATEGORY.PLACEHOLDER'
                )
              }}
            </option>
            <option value="MARKETING">
              {{
                t(
                  'INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.FORM.CATEGORY.OPTIONS.MARKETING'
                )
              }}
            </option>
            <option value="UTILITY">
              {{
                t(
                  'INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.FORM.CATEGORY.OPTIONS.UTILITY'
                )
              }}
            </option>
            <option value="AUTHENTICATION">
              {{
                t(
                  'INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.FORM.CATEGORY.OPTIONS.AUTHENTICATION'
                )
              }}
            </option>
          </select>
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.FORM.LANGUAGE.LABEL') }}
          </label>
          <input
            v-model="form.language"
            type="text"
            class="mb-0"
            :placeholder="
              t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.FORM.LANGUAGE.PLACEHOLDER')
            "
          />
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.FORM.COMPONENTS.LABEL') }}
          </label>
          <textarea
            v-model="form.components"
            rows="6"
            class="mb-0 font-mono text-sm"
            :placeholder="
              t(
                'INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.FORM.COMPONENTS.PLACEHOLDER'
              )
            "
          />
        </div>
        <p v-if="formError" class="text-sm text-red-600">{{ formError }}</p>
      </div>
    </Dialog>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.DELETE')"
      :description="t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.DELETE_CONFIRM')"
      :confirm-button-label="
        t('INBOX_MGMT.WHATSAPP_TEMPLATES_PANEL.DELETE_BUTTON')
      "
      :is-loading="uiFlags.isDeleting"
      @confirm="handleDeleteConfirm"
    />

    <VariableMappingModal
      v-if="selectedMappingTemplate"
      ref="mappingModalRef"
      :template="selectedMappingTemplate"
      :inbox-id="inbox.id"
    />
  </div>
</template>
